import { useMemo, useState } from 'react'
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, List, ListItem, ListItemText, Stack,
  TextField, Typography,
} from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'

const DevolutionEquipmentDialog = ({
  open, onClose, onConfirm, equipments, loading, error, alreadyPresentIds = [],
}) => {
  const [search, setSearch] = useState('')
  const [chosen, setChosen] = useState([])
  const [leftChecks, setLeftChecks] = useState([])
  const [rightChecks, setRightChecks] = useState([])

  const initialAvailable = useMemo(() => {
    const present = new Set(alreadyPresentIds.map(String))
    return equipments.filter((item) => !present.has(String(item.id)))
  }, [equipments, alreadyPresentIds])
  const leftItems = initialAvailable.filter((item) => !chosen.some((selected) => String(selected.id) === String(item.id)))
  const filterItems = (items) => items.filter((item) => `${item.number ?? ''} ${item.name ?? ''}`.toLowerCase().includes(search.toLowerCase()))
  const visibleLeft = filterItems(leftItems)
  const visibleRight = filterItems(chosen)

  const toggle = (id, side) => {
    const setter = side === 'left' ? setLeftChecks : setRightChecks
    setter((ids) => ids.includes(id) ? ids.filter((value) => value !== id) : [...ids, id])
  }
  const toggleAllVisible = (side, checked) => {
    const items = side === 'left' ? visibleLeft : visibleRight
    const setter = side === 'left' ? setLeftChecks : setRightChecks
    setter((ids) => checked
      ? [...new Set([...ids, ...items.map((item) => item.id)])]
      : ids.filter((id) => !items.some((item) => String(item.id) === String(id))))
  }
  const transfer = (side) => {
    const isLeft = side === 'left'
    const selectedIds = isLeft ? leftChecks : rightChecks
    const source = isLeft ? leftItems : chosen
    const moving = source.filter((item) => selectedIds.some((id) => String(id) === String(item.id)))
    if (isLeft) {
      setChosen((items) => [...items, ...moving.filter((item) => !items.some((current) => String(current.id) === String(item.id)))])
      setLeftChecks([])
    } else {
      setChosen((items) => items.filter((item) => !selectedIds.some((id) => String(id) === String(item.id))))
      setRightChecks([])
    }
  }
  const close = () => {
    setSearch(''); setChosen([]); setLeftChecks([]); setRightChecks([])
    onClose()
  }
  const addAllChosen = () => {
    onConfirm(chosen)
    close()
  }

  const renderPanel = (title, items, side, checks) => {
    const allSelected = items.length > 0 && items.every((item) => checks.some((id) => String(id) === String(item.id)))
    const partiallySelected = items.some((item) => checks.some((id) => String(id) === String(item.id)))
    return (
      <Box sx={{ flex: 1, minWidth: 0, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 1, bgcolor: 'action.hover' }}>
          <Typography variant="subtitle2">{title}</Typography>
          <Typography variant="caption" color="text.secondary">{items.length}</Typography>
        </Stack>
        <FormControlLabel sx={{ px: 1, my: 0 }} control={
          <Checkbox size="small" checked={allSelected} indeterminate={partiallySelected && !allSelected}
            disabled={!items.length || loading || !!error}
            onChange={(event) => toggleAllVisible(side, event.target.checked)} />
        } label="Selecionar todos / Desselecionar todos" />
        <List dense aria-label={title} sx={{ height: 250, overflowY: 'auto', borderTop: '1px solid', borderColor: 'divider' }}>
          {items.map((item) => (
            <ListItem key={item.id} disablePadding>
              <FormControlLabel sx={{ m: 0, px: 1, width: '100%' }} control={
                <Checkbox size="small" checked={checks.some((id) => String(id) === String(item.id))} onChange={() => toggle(item.id, side)} />
              } label={<ListItemText primary={`${item.number ?? '—'} — ${item.name ?? 'Equipamento'}`} />} />
            </ListItem>
          ))}
          {!items.length && <ListItem><ListItemText secondary="Nenhum equipamento neste painel." /></ListItem>}
        </List>
      </Box>
    )
  }

  const leftSelected = leftChecks.filter((id) => visibleLeft.some((item) => String(item.id) === String(id))).length
  const rightSelected = rightChecks.filter((id) => visibleRight.some((item) => String(item.id) === String(id))).length

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="md" aria-labelledby="devolution-dialog-title">
      <DialogTitle id="devolution-dialog-title">Selecionar equipamentos para devolução</DialogTitle>
      <DialogContent dividers>
        <Typography color="text.secondary" sx={{ mb: 2 }}>Transfira para a lista da direita os equipamentos que serão devolvidos.</Typography>
        <TextField autoFocus fullWidth size="small" label="Buscar equipamento" value={search} onChange={(event) => setSearch(event.target.value)} sx={{ mb: 2 }} />
        {loading ? <Typography role="status" sx={{ py: 3, textAlign: 'center' }}>Carregando equipamentos…</Typography>
          : error ? <Alert severity="error">Não foi possível carregar os equipamentos. Feche e tente novamente.</Alert>
            : equipments.length === 0 ? <Alert severity="info">Não há equipamentos elegíveis para devolução deste cliente.</Alert>
              : <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="stretch">
                {renderPanel('Disponíveis', visibleLeft, 'left', leftChecks)}
                <Stack direction={{ xs: 'row', sm: 'column' }} justifyContent="center" alignItems="center" spacing={1}>
                  <Button aria-label="Mover selecionados para devolução" variant="outlined" disabled={!leftSelected} onClick={() => transfer('left')} sx={{ minWidth: { xs: 48, sm: 42 } }}><ArrowForwardIcon /></Button>
                  <Button aria-label="Remover selecionados da devolução" variant="outlined" disabled={!rightSelected} onClick={() => transfer('right')} sx={{ minWidth: { xs: 48, sm: 42 } }}><ArrowBackIcon /></Button>
                </Stack>
                {renderPanel('Selecionados para devolução', visibleRight, 'right', rightChecks)}
              </Stack>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={close}>Cancelar</Button>
        <Button variant="contained" disabled={loading || !!error || !chosen.length} onClick={addAllChosen}>Adicionar {chosen.length}</Button>
      </DialogActions>
    </Dialog>
  )
}

export default DevolutionEquipmentDialog
